import { useAppDispatch, useAppSelector } from "../../app";
import { useCallback } from "react";
import {
  CategorizationAnnotation,
  ICategorization,
} from "../../configMap/interfaces/category";
import { KeyByAny } from "../../documentSet/interfaces/overview";
import {
  CategorizationEvent,
  EventCategorizationResponse,
  EventTriggerType,
} from "../interfaces/categorizationEvents";
import selectAnnotationReducer from "../../annotation/selectors/annotationReducerSelector";
import {
  useLazyCallEventQuery,
  useUpsertDerivedRecordsMutation,
} from "../store/metadataApi";
import {
  addLoadingCategorizationIds,
  removeLoadingCategorizationIds,
} from "../store/metadataSlice";
import { notification } from "antd";
import {
  createCategorizationAnnotationFromResponse,
  getBodyParams,
  getParamValue,
  getQueryParams,
} from "../utils/categorizationEvents";
import { Annotation } from "../../annotator/interfaces/annotation";
import { isDocumentSetDisabled } from "../../documentSet/selectors/documentSetDisabledSelector";
import { DocumentSetData } from "../../documentSet/interfaces/documentSet";

const useCategorizationEvents = () => {
  const dispatch = useAppDispatch();

  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );

  const attribute = activeDocumentSet?.input?.attribute;

  const documentId = useAppSelector(
    (state) => state.documentReducer.activeDocument?.id,
  );

  const entityAnnotations = useAppSelector(selectAnnotationReducer);

  const categorizationAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState.categorizationAnnotations,
  );

  const { user } = useAppSelector((state) => state.userReducer);
  const isAnnotationMode = false;
  const pdfPagesLength = useAppSelector(
    (state) => state.documentReducer.pdf?.pages.length,
  );

  const [callEvent] = useLazyCallEventQuery();
  const [upsertDerivedRecords] = useUpsertDerivedRecordsMutation();

  const setLoadingCategorizations = useCallback(
    (
      isLoading: boolean,
      responseProperties: Array<EventCategorizationResponse>,
    ) => {
      const categorizationIds = responseProperties.map((rp) =>
        rp.categorizationId.toLowerCase(),
      );

      if (isLoading) {
        dispatch(addLoadingCategorizationIds(categorizationIds));
      } else {
        dispatch(removeLoadingCategorizationIds(categorizationIds));
      }
    },
    [dispatch],
  );

  const triggerEvent = useCallback(
    // Deps are deliberately narrower than the compiler can infer: it widens
    // `user?.organization?.id` to the whole `user` object. Widening would
    // re-create this callback whenever the Redux `user` object is re-created
    // with an unchanged organization id, re-triggering the caller effects that
    // depend on it. The narrow dep is the correct one.
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- narrow optional-chain dep is intentional; widening re-triggers caller effects
    async (
      event: CategorizationEvent,
      value: string,
      categorization: ICategorization,
      onLoad: boolean = false,
      givenCas?: Array<CategorizationAnnotation>,
      givenEntityAnnotations?: Array<Annotation>,
      givenAttribute?: KeyByAny,
      givenDocumentId?: string,
      visibleValue?: string,
    ) => {
      const { request, responseProperties, triggerType } = event;
      const { urlParam, bodyParams, queryParams, httpMethod, endpoint } =
        request;

      if (!endpoint || !triggerType || !httpMethod || !responseProperties) {
        return;
      }

      const currentCategorizationAnnotations =
        givenCas ?? categorizationAnnotations;
      const currentEntityAnnotations =
        givenEntityAnnotations ?? entityAnnotations;
      const currentAttribute = givenAttribute ?? attribute;
      const currentDocumentId = givenDocumentId ?? documentId;

      let urlParamValue =
        getParamValue(
          urlParam,
          value,
          categorization.id,
          currentCategorizationAnnotations || [],
          currentEntityAnnotations,
          currentAttribute,
          visibleValue,
        ) ?? "";
      if (urlParamValue) {
        urlParamValue = `/${urlParamValue}`;
      }

      const bodyParameters = getBodyParams(
        bodyParams,
        value,
        categorization,
        currentCategorizationAnnotations || [],
        currentEntityAnnotations,
        currentAttribute,
        visibleValue,
      );
      const queryParameters = getQueryParams(
        queryParams,
        value,
        categorization,
        currentCategorizationAnnotations || [],
        currentEntityAnnotations,
        currentAttribute,
        visibleValue,
      );

      const body = {
        endpoint: `${endpoint}${urlParamValue}${queryParameters}`,
        httpMethod,
        bodyParams: bodyParameters,
      };

      try {
        setLoadingCategorizations(true, responseProperties);
        const response = await callEvent(body).unwrap();

        await createCategorizationAnnotationFromResponse(
          responseProperties,
          response,
          dispatch,
          currentDocumentId!,
          currentCategorizationAnnotations || [],
          onLoad,
          categorization,
          upsertDerivedRecords,
          user?.organization?.id,
        );
      } catch (error: unknown) {
        console.error(error);
        if (!onLoad) {
          const title =
            (error as { data?: { error?: string } })?.data?.error ||
            "We encountered an issue while fetching data for your selection.";
          notification.error({ title });
        }
      } finally {
        setLoadingCategorizations(false, responseProperties);
      }
    },
    [
      callEvent,
      dispatch,
      setLoadingCategorizations,
      categorizationAnnotations,
      entityAnnotations,
      attribute,
      documentId,
      upsertDerivedRecords,
      user?.organization?.id,
    ],
  );

  const callEvents = useCallback(
    async (
      eventTriggerType: EventTriggerType,
      categorization: ICategorization,
      value: string = "",
      visibleValue?: string,
    ) => {
      const events = categorization.events;
      if (!events?.length) {
        return;
      }

      // Filter the events to include only those with the specified trigger type
      const filteredEvents = events.filter(
        (event) => event.triggerType === eventTriggerType,
      );

      await Promise.all(
        filteredEvents.map((event) =>
          triggerEvent(
            event,
            value,
            categorization,
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            visibleValue,
          ),
        ),
      );
    },
    [triggerEvent],
  );

  const triggerOnLoadCategorizationEvents = useCallback(
    (
      categorizations: Array<ICategorization> | undefined,
      currentCategorizationAnnotations: Array<CategorizationAnnotation>,
      annotations: Array<Annotation>,
      attribute: KeyByAny,
      documentId: string,
      activeDocumentSet?: DocumentSetData,
    ) => {
      const disabled = isDocumentSetDisabled(
        activeDocumentSet,
        user,
        isAnnotationMode,
        pdfPagesLength,
      );
      if (!categorizations?.length || disabled) {
        return;
      }

      for (const categorization of categorizations) {
        const events = categorization.events;
        if (!events?.length) {
          continue;
        }

        const onLoadEvents = events?.filter((event) => event.triggerOnLoad);

        const ca = currentCategorizationAnnotations.find(
          (ca) => ca.index === 1 && ca.categorizationId === categorization.id,
        );

        if (!onLoadEvents?.length) {
          continue;
        }

        onLoadEvents?.map((event) =>
          triggerEvent(
            event,
            ca?.value ?? "",
            categorization,
            true,
            currentCategorizationAnnotations,
            annotations,
            attribute,
            documentId,
            ca?.visibleValue,
          ),
        );
      }
    },
    [triggerEvent, user, isAnnotationMode, pdfPagesLength],
  );

  return {
    callEvents,
    triggerOnLoadCategorizationEvents,
  };
};

export type CallEvents = ReturnType<
  typeof useCategorizationEvents
>["callEvents"];

export default useCategorizationEvents;
